import * as T from 'three';
// Solid texture for play sand (Perlin 1985; Peachey 1985): a grain's colour
// comes from where it sat in the loaf, so every cut, dent and crumb shows the
// inside. Pastel layers run through the loaf, and a round face runs along its
// core like a slice-and-see candy. The face changes expression every `period`
// along the loaf, and the two original ends stay plain until the first cut.
const GLSL=`
uniform vec3 sandMin;uniform vec3 sandMax;uniform float sandPeriod;varying vec3 vSandRest;
vec3 sandLinear(vec3 c){return pow(c,vec3(2.2));}
float sandHash(vec3 p){p=fract(p*.3183099+.1);p*=17.;return fract(p.x*p.y*p.z*(p.x+p.y+p.z));}
float sandNoise(vec3 x){vec3 i=floor(x),f=fract(x);f=f*f*(3.-2.*f);
  return mix(mix(mix(sandHash(i),sandHash(i+vec3(1,0,0)),f.x),mix(sandHash(i+vec3(0,1,0)),sandHash(i+vec3(1,1,0)),f.x),f.y),
    mix(mix(sandHash(i+vec3(0,0,1)),sandHash(i+vec3(1,0,1)),f.x),mix(sandHash(i+vec3(0,1,1)),sandHash(i+vec3(1,1,1)),f.x),f.y),f.z);}
float sandFill(float d,float aa){return 1.-smoothstep(-aa,aa,d);}
float sandRing(vec2 p,vec2 c,float r,float w){return abs(length(p-c)-r)-w;}
vec3 sandLayer(float t){
  const vec3 L0=vec3(.80,.72,1.),L1=vec3(.66,.91,.80),L2=vec3(1.,.88,.55),L3=vec3(1.,.71,.79),L4=vec3(.67,.85,1.);
  float x=clamp(t,0.,.9999)*5.,f=fract(x);float i=floor(x);
  vec3 a=i<1.?L0:i<2.?L1:i<3.?L2:i<4.?L3:L4,b=i<1.?L1:i<2.?L2:i<3.?L3:L4;
  return sandLinear(mix(a,b,smoothstep(.9,1.,f)*step(i,3.5)));
}
vec3 sandColor(vec3 p){
  vec3 size=sandMax-sandMin;
  float wobble=.035*sin(p.x*7.+p.z*5.)+.02*sin(p.z*11.-p.x*3.);
  vec3 col=sandLayer((p.y-sandMin.y)/size.y+wobble);
  vec2 centre=vec2((sandMin.z+sandMax.z)*.5,sandMin.y+size.y*.5);
  float R=min(size.y,size.z)*.37;
  vec2 u=(vec2(p.z,p.y)-centre)/R;float r=length(u);
  float cap=size.y*.07;
  if(p.x>sandMin.x+cap&&p.x<sandMax.x-cap&&r<1.06){
    float aa=max(fwidth(r)*1.3,.006);
    vec3 cream=sandLinear(vec3(1.,.96,.90)),rim=sandLinear(vec3(.99,.62,.74)),ink=sandLinear(vec3(.36,.22,.22)),blush=sandLinear(vec3(1.,.62,.70)),tongue=sandLinear(vec3(1.,.47,.58));
    col=mix(col,cream,sandFill(r-1.,aa));
    col=mix(col,rim,sandFill(abs(r-.955)-.045,aa));
    float face=mod(floor((p.x-sandMin.x)/sandPeriod),5.);
    vec2 e=vec2(abs(u.x),u.y);
    float eyes=0.,glint=0.,mouth=0.,tongueMask=0.;
    if(face<.5){ // smile
      eyes=sandFill(length(e-vec2(.36,.17))-.13,aa);glint=sandFill(length(e-vec2(.32,.22))-.04,aa);
      mouth=sandFill(sandRing(u,vec2(0.,.04),.36,.045),aa)*step(u.y,-.16);
    }else if(face<1.5){ // wink
      eyes=u.x<0.?sandFill(length(e-vec2(.36,.17))-.13,aa):sandFill(sandRing(u,vec2(.36,.06),.13,.038),aa)*step(.09,u.y);
      glint=u.x<0.?sandFill(length(e-vec2(.32,.22))-.04,aa):0.;
      mouth=sandFill(sandRing(u,vec2(0.,.04),.36,.045),aa)*step(u.y,-.16);
    }else if(face<2.5){ // surprised
      eyes=sandFill(length(e-vec2(.36,.2))-.11,aa);glint=sandFill(length(e-vec2(.33,.24))-.035,aa);
      mouth=sandFill(sandRing(u,vec2(0.,-.34),.1,.042),aa);
    }else if(face<3.5){ // sleepy
      eyes=sandFill(sandRing(e,vec2(.36,.27),.13,.035),aa)*step(e.y,.25);
      mouth=sandFill(sandRing(u,vec2(0.,-.14),.18,.04),aa)*step(u.y,-.24);
    }else{ // happy with a tongue
      eyes=sandFill(sandRing(e,vec2(.36,.08),.13,.038),aa)*step(.1,e.y);
      float open=sandFill(length(u-vec2(0.,-.2))-.24,aa)*step(u.y,-.2);
      mouth=open;tongueMask=open*sandFill(length(u-vec2(0.,-.4))-.13,aa);
    }
    float cheeks=sandFill(length((e-vec2(.62,-.1))/vec2(.17,.1))-1.,aa*4.);
    col=mix(col,blush,cheeks*.85);
    col=mix(col,ink,max(eyes,mouth*(1.-tongueMask)));col=mix(col,tongue,tongueMask);col=mix(col,vec3(1.),glint);
  }
  return col*(.92+.13*sandNoise(p*57.));
}`;
export function makeSandMaterial({min,max,period}){
  const material=new T.MeshStandardMaterial({color:'#ffffff',roughness:.93,metalness:0});
  const uniforms={sandMin:{value:new T.Vector3(...min)},sandMax:{value:new T.Vector3(...max)},sandPeriod:{value:period}};
  material.customProgramCacheKey=()=> 'sand-solid-v1';
  material.onBeforeCompile=shader=>{
    Object.assign(shader.uniforms,uniforms);
    shader.vertexShader='attribute vec3 sandRest;\nvarying vec3 vSandRest;\n'+shader.vertexShader.replace('#include <begin_vertex>','#include <begin_vertex>\n  vSandRest=sandRest;');
    shader.fragmentShader=GLSL+'\n'+shader.fragmentShader
      .replace('#include <color_fragment>','#include <color_fragment>\n  diffuseColor.rgb*=sandColor(vSandRest);')
      // Grain: a fine bump from the same noise, as screen-space derivatives.
      .replace('#include <normal_fragment_maps>',`#include <normal_fragment_maps>
        {float g=sandNoise(vSandRest*90.);vec3 sx=dFdx(-vViewPosition),sy=dFdy(-vViewPosition);
         vec3 r1=cross(sy,normal),r2=cross(normal,sx);float det=dot(sx,r1);
         if(abs(det)>1e-12){vec3 grad=sign(det)*(dFdx(g)*r1+dFdy(g)*r2)*.0035;normal=normalize(abs(det)*normal-grad);}}`);
  };
  material.userData.sandUniforms=uniforms;
  return material;
}
